/* eslint-disable import/no-cycle,no-param-reassign */
import Vue from 'vue';
import LogoReviewService, { absoluteApiUrl } from '@/services/logoReview.service';
import { FINISHED_LOADING, IS_LOADING } from '@/store/modules/layout/layoutActions.type';
import {
  FETCH_LOGO_REVIEW_QUEUE,
  FETCH_LOGO_REVIEW_ITEM,
  FETCH_LOGO_REVIEW_CANDIDATES,
  FETCH_LOGO_REVIEW_STATS,
  ACCEPT_LOGO_REVIEW,
  SKIP_LOGO_REVIEW,
  TEXT_ICON_LOGO_REVIEW,
  FLAG_LOGO_REVIEW,
} from './logoReviewActions.type';
import {
  SET_LOGO_REVIEW_QUEUE,
  SET_LOGO_REVIEW_STATS,
  SET_LOGO_REVIEW_CANDIDATES,
  REMOVE_LOGO_REVIEW_ITEM,
} from './logoReviewMutations.type';

export default {
  state: {
    // The claimed batch, in review order.
    items: [],
    // Candidates keyed by unlisted site id, so navigating back to an item
    // already looked at does not re-crawl it.
    candidatesBySite: {},
    // Whether the crawl for a given site failed outright, so the UI can offer
    // a retry instead of implying the site has no logo.
    crawlStatusBySite: {},
    stats: {
      pending_domains: 0,
      priority_domains: 0,
      claimed: 0,
    },
  },
  getters: {
    logoReviewItems(state) {
      return state.items;
    },
    logoReviewStats(state) {
      return state.stats;
    },
    logoReviewCandidatesFor: state => id => state.candidatesBySite[id] || null,
    logoReviewCrawlStatusFor: state => id => state.crawlStatusBySite[id] || null,
  },
  mutations: {
    [SET_LOGO_REVIEW_QUEUE](state, items) {
      Vue.set(state, 'items', items);
    },
    [SET_LOGO_REVIEW_STATS](state, stats) {
      Vue.set(state, 'stats', stats);
    },
    [SET_LOGO_REVIEW_CANDIDATES](state, {
      id, candidates, crawlFailed, crawlError, site,
    }) {
      Vue.set(state.candidatesBySite, id, candidates);
      Vue.set(state.crawlStatusBySite, id, { failed: Boolean(crawlFailed), error: crawlError || null });

      // The crawl is what discovers the page title and the post-redirect URL,
      // so the queue item claimed before it ran has neither. Merge them back
      // in rather than leaving the view reading a staler copy of the row.
      if (site) {
        const index = state.items.findIndex(item => item.id === id);
        if (index !== -1) {
          Vue.set(state.items, index, { ...state.items[index], ...site });
        }
      }
    },
    [REMOVE_LOGO_REVIEW_ITEM](state, id) {
      Vue.set(state, 'items', state.items.filter(item => item.id !== id));
      Vue.delete(state.candidatesBySite, id);
      Vue.delete(state.crawlStatusBySite, id);
    },
  },
  actions: {
    [FETCH_LOGO_REVIEW_QUEUE](context, limit) {
      return new Promise((resolve, reject) => {
        context.dispatch(IS_LOADING).then(() => {
          LogoReviewService.queue(limit)
            .then((response) => {
              context.commit(SET_LOGO_REVIEW_QUEUE, response.data.data.items);
              context.commit(SET_LOGO_REVIEW_STATS, response.data.data.stats);
              context.dispatch(FINISHED_LOADING);
              resolve(response.data);
            })
            .catch((error) => {
              context.dispatch(FINISHED_LOADING);
              reject(error);
            });
        });
      });
    },
    /**
     * Load a single site as the whole "batch", for redoing its logo. The
     * rest of the page then works exactly as it does on queue items.
     */
    [FETCH_LOGO_REVIEW_ITEM](context, id) {
      return new Promise((resolve, reject) => {
        context.dispatch(IS_LOADING).then(() => {
          LogoReviewService.item(id)
            .then((response) => {
              context.commit(SET_LOGO_REVIEW_QUEUE, [response.data.data.item]);
              context.dispatch(FINISHED_LOADING);
              resolve(response.data);
            })
            .catch((error) => {
              context.dispatch(FINISHED_LOADING);
              reject(error);
            });
        });
      });
    },
    /**
     * Deliberately does not toggle the global loading overlay: the UI
     * prefetches the next site's candidates in the background while the
     * operator is still working on the current one.
     */
    [FETCH_LOGO_REVIEW_CANDIDATES](context, { id, refresh }) {
      return new Promise((resolve, reject) => {
        LogoReviewService.candidates(id, refresh)
          .then((response) => {
            context.commit(SET_LOGO_REVIEW_CANDIDATES, {
              id,
              // Resolve the signed relative paths once, here, so the view
              // only ever deals in loadable URLs.
              candidates: (response.data.data.candidates || []).map(candidate => ({
                ...candidate,
                url: absoluteApiUrl(candidate.url),
              })),
              crawlFailed: response.data.data.crawl_failed,
              crawlError: response.data.data.crawl_error,
              site: response.data.data.site,
            });
            resolve(response.data);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    [FETCH_LOGO_REVIEW_STATS](context) {
      return new Promise((resolve, reject) => {
        LogoReviewService.stats()
          .then((response) => {
            context.commit(SET_LOGO_REVIEW_STATS, response.data.data);
            resolve(response.data);
          })
          .catch(error => reject(error));
      });
    },
    [ACCEPT_LOGO_REVIEW](context, { id, formData }) {
      return new Promise((resolve, reject) => {
        LogoReviewService.accept(id, formData)
          .then((response) => {
            context.commit(REMOVE_LOGO_REVIEW_ITEM, id);
            resolve(response.data);
          })
          .catch(error => reject(error));
      });
    },
    [TEXT_ICON_LOGO_REVIEW](context, id) {
      return new Promise((resolve, reject) => {
        LogoReviewService.applyTextIcon(id)
          .then((response) => {
            context.commit(REMOVE_LOGO_REVIEW_ITEM, id);
            resolve(response.data);
          })
          .catch(error => reject(error));
      });
    },
    [SKIP_LOGO_REVIEW](context, id) {
      return new Promise((resolve, reject) => {
        LogoReviewService.skip(id)
          .then((response) => {
            context.commit(REMOVE_LOGO_REVIEW_ITEM, id);
            resolve(response.data);
          })
          .catch(error => reject(error));
      });
    },
    [FLAG_LOGO_REVIEW](context, { id, reason }) {
      return new Promise((resolve, reject) => {
        LogoReviewService.flag(id, reason)
          .then((response) => {
            context.commit(REMOVE_LOGO_REVIEW_ITEM, id);
            resolve(response.data);
          })
          .catch(error => reject(error));
      });
    },
  },
};
